import { PageHeading } from '@src/components/PageHeading/PageHeading';
import { useHydrated } from '@src/utils/useHydrated';
import { trackUmamiEvent } from '@src/utils/umami';
import { useNavigate } from 'react-router-dom';
import { LandscapeIntro } from '@src/components/LandingLayout/LandingLayout';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  QUICK_BOX_MAX_PARTICIPANTS,
  QUICK_BOX_MIN_PARTICIPANTS,
  validateQuickBoxCreateInput,
} from '@lub0v/santa-utils';
import {
  Button,
  Card,
  CloseIcon,
  Cluster,
  Columns,
  BoxShell,
  Container,
  Expandable,
  ExpandableGroup,
  HintError,
  Link,
  NamesInput,
  ParticipantFace,
  ParticipantTable,
  ParticipantTableRowList,
  PreviewCard,
  Prose,
  Row,
  LinkIcon,
  MailIcon,
  Stack,
  Typography,
  UserIcon,
  showDialog,
  PARTICIPANT_TABLE_SEARCH_THRESHOLD,
  useParticipantTableViewport,
  type CardTone,
  type NamesProblem,
} from '@src/components';
import { ExclusionEditor, type ExclusionOption } from '@src/pages/RandomizerPage/ExclusionEditor';
import {
  clearExclusions,
  draftToCreateInput,
  emptyDraft,
  excludedFor,
  hasAnyExclusion,
  isDrawImpossible,
  isMutual,
  pruneExclusions,
  setExclusionsFor,
  setMutual,
  type DrawDraft,
} from '@src/pages/RandomizerPage/draft';
import { storeOrganizerAccess } from '@src/access/organizerAccess';

import { FaqJsonLd } from '@src/seo/FaqJsonLd';
import { santaClient } from '@src/state/client';
import { useAppDispatch } from '@src/state/hooks';
import { loadSession } from '@src/state/sessionSlice';
import './RandomizerPage.css';

const HOW_STEPS = [
  { id: 'one', step: 1, tone: 'support' as const },
  { id: 'two', step: 2, tone: 'accent' as const },
  { id: 'three', step: 3, tone: 'info' as const },
] as const;
const BENEFIT_CARDS = [
  { id: 'one', tone: 'accent', icon: <UserIcon /> },
  { id: 'two', tone: 'info', icon: <MailIcon /> },
  { id: 'three', tone: 'support', icon: <LinkIcon /> },
] as const satisfies ReadonlyArray<{ id: string; tone: CardTone; icon: React.ReactNode }>;

type FaqItem = { question: string; answer: string };

/** Which name the editor is open for, and what to restore if it is cancelled. */
type EditorState = {
  name: string;
  snapshot: Record<string, string[]>;
  query: string;
};

/** Answers are authored with blank lines between paragraphs. */
function paragraphs(text: string): string[] {
  return text.split('\n\n');
}

export function RandomizerPage() {
  const hydrated = useHydrated();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [draft, setDraft] = useState<DrawDraft>(() =>
    emptyDraft(t('randomizer.gameName', { year: new Date().getFullYear() })),
  );
  const [problem, setProblem] = useState<NamesProblem | null>(null);
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [drawError, setDrawError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const faq = t('randomizer.faq.items', { returnObjects: true }) as FaqItem[];
  const count = draft.names.length;
  const namesReady =
    count >= QUICK_BOX_MIN_PARTICIPANTS && count <= QUICK_BOX_MAX_PARTICIPANTS && !problem;

  const getExclusionSearchText = useCallback((name: string) => name, []);
  const { tableRef: participantTableRef, ...participantTable } = useParticipantTableViewport({
    enabled: true,
    items: draft.names,
    mobileMinRows: 3,
    desktopMinRows: 3,
    getSearchText: getExclusionSearchText,
    showSearch: count > PARTICIPANT_TABLE_SEARCH_THRESHOLD,
  });

  const editorOptions = useMemo<ExclusionOption[]>(() => {
    if (!editor) {
      return [];
    }
    const excluded = excludedFor(draft, editor.name);
    return draft.names
      .filter((name) => name !== editor.name)
      .map((name) => ({
        name,
        selected: excluded.includes(name),
        mutual: isMutual(draft, editor.name, name),
      }));
  }, [draft, editor]);

  /*
   * The dialog is re-shown on every toggle. Everything it displays lives in
   * page state, so re-showing renders exactly what a normal re-render would —
   * and the save button can stay disabled while the selection has no solution.
   */
  useEffect(() => {
    if (!editor) {
      return;
    }
    const impossible = isDrawImpossible(draft);
    showDialog({
      title: t('randomizer.exclusions.editorTitle', { name: editor.name }),
      children: (
        <ExclusionEditor
          options={editorOptions}
          subtitle={t('randomizer.exclusions.editorSubtitle', { name: editor.name })}
          query={editor.query}
          onQueryChange={(query) => setEditor((current) => current && { ...current, query })}
          searchLabel={t('randomizer.exclusions.search')}
          peopleLabel={t('randomizer.exclusions.people')}
          mutualLabel={t('randomizer.exclusions.mutual')}
          mutualHint={t('randomizer.exclusions.mutualHint')}
          error={impossible ? 'randomizer.impossible' : undefined}
          onToggle={(name, selected) => {
            setDraft((current) => {
              const existing = excludedFor(current, editor.name);
              return setExclusionsFor(
                current,
                editor.name,
                selected ? [...existing, name] : existing.filter((item) => item !== name),
              );
            });
          }}
          onToggleMutual={(name, mutual) => {
            setDraft((current) => setMutual(current, editor.name, name, mutual));
          }}
          testId="exclusion-editor"
        />
      ),
      cancel: t('randomizer.exclusions.cancel'),
      submit: t('randomizer.exclusions.save'),
      submitDisabled: impossible,
      onSubmit: () => setEditor(null),
      // Cancel has to close the editor as well as roll the draft back —
      // leaving it open would have this effect put the dialog straight back.
      onCancel: () => {
        setDraft((current) => ({ ...current, exclusionsByName: editor.snapshot }));
        setEditor(null);
      },
      testId: 'exclusion-dialog',
        mobileFullScreen: true,
    });
  }, [draft, editor, editorOptions, t]);

  async function runDraw() {
    if (creating) {
      return;
    }
    const input = draftToCreateInput(draft);
    if (validateQuickBoxCreateInput(input)?.errors?.includes('RANDOMIZER_FAILED')) {
      setDrawError('randomizer.impossible');
      return;
    }
    setCreating(true);
    setDrawError(null);
    try {
      const result = await santaClient.createQuickBox(input);
      const key = result.data?.box?.key;
      if (result.ok && key) {
        trackUmamiEvent('quick_draw_created');
        const organizerAccess = result.data?.organizerAccess;
        if (organizerAccess) {
          storeOrganizerAccess(key, organizerAccess);
        }
        await dispatch(loadSession());
        navigate(`/box/${key}`);
        return;
      }
      const code = result.error?.message;
      setDrawError(
        code === 'RANDOMIZER_FAILED' ? 'randomizer.impossible' : code || 'errors.UNKNOWN',
      );
    } finally {
      setCreating(false);
    }
  }

  function confirmDraw() {
    // The client check runs the same algorithm the API does, so say so here
    // rather than after a round trip.
    if (isDrawImpossible(draft)) {
      setDrawError('randomizer.impossible');
      return;
    }
    showDialog({
      title: t('randomizer.confirm.title'),
      children: t('randomizer.confirm.body'),
      cancel: t('randomizer.confirm.no'),
      submit: t('randomizer.confirm.yes'),
      onSubmit: () => runDraw(),
      testId: 'randomizer-confirm',
    });
  }

  function openEditor(name: string) {
    setEditor({
      name,
      snapshot: Object.fromEntries(
        Object.entries(draft.exclusionsByName).map(([key, value]) => [key, [...value]]),
      ),
      query: '',
    });
  }

  function exclusionSummary(name: string): string {
    const excluded = excludedFor(draft, name);
    return excluded.length === 0
      ? t('randomizer.exclusions.none')
      : t('randomizer.exclusions.givesNot', { names: excluded.join(', ') });
  }

  function setNames(names: string[]) {
    setDraft((current) => pruneExclusions({ ...current, names }));
  }

  function removeName(name: string) {
    setDraft((current) =>
      pruneExclusions({ ...current, names: current.names.filter((item) => item !== name) }),
    );
    setProblem(null);
  }

  return (
    <BoxShell width="full" testId="randomizer">
      <Stack gap={16} wide className="randomizer-step">
        <LandscapeIntro>
          <Stack gap={3} align="center" as="header" wide testId="randomizer-hero">
            <PageHeading title={t('randomizer.title')} subtitle={t('randomizer.intro')}><Typography as="p" role="meta/helper">{t('randomizer.benefitLine')}</Typography></PageHeading>
          </Stack>

          <Stack gap={4} wide className="randomizer-form" testId="randomizer-form">
            <div
              className="participant-table-fill participant-table-fill--desktop-page"
              data-participant-table-fill
            >
              <ParticipantTable
                ref={participantTableRef}
                actions={
                  <Stack gap={3} align="end">
                    <HintError error={drawError} testId="randomizer-draw-error" />
                    <Button
                      variant="primary"
                      size="large"
                      disabled={!namesReady || creating}
                      testId="randomizer-draw"
                      onClick={confirmDraw}
                    >
                      {creating ? t('randomizer.creating') : t('randomizer.exclusions.draw')}
                    </Button>
                  </Stack>
                }
                label={t('randomizer.names.label')}
                compose={
                  <NamesInput
                    disabled={!hydrated}
                    names={draft.names}
                    onNamesChange={setNames}
                    onProblem={setProblem}
                    placeholder={t('randomizer.names.placeholder')}
                    hint={t('randomizer.names.hint')}
                    error={problem ? t(`randomizer.problem.${problem}`) : undefined}
                    removeLabel={(name) => t('randomizer.names.remove', { name })}
                    maxNames={QUICK_BOX_MAX_PARTICIPANTS}
                    rows={1}
                    showTags={false}
                    testId="randomizer-names"
                  />
                }
                composeDivider={false}
                showSearch={count > 0 && participantTable.showSearch}
                rangeEnd={
                  hasAnyExclusion(draft) ? (
                    <Link
                      variant="quiet"
                      testId="randomizer-exclusions-reset"
                      onClick={() => setDraft(clearExclusions)}
                    >
                      {t('randomizer.exclusions.reset')}
                    </Link>
                  ) : undefined
                }
                search={{
                  value: participantTable.query,
                  onChange: participantTable.setSearch,
                  placeholder: t('box.select.search'),
                  clearLabel: t('box.organizer.searchClear'),
                }}
                isEmpty={count > 0 && participantTable.isEmpty}
                empty={
                  <Typography as="p" role="body/base" testId="randomizer-exclusions-empty">
                    {t('box.organizer.noResults')}
                  </Typography>
                }
                pagination={{
                  page: participantTable.page,
                  pageCount: participantTable.pageCount,
                  onChange: participantTable.setPage,
                  total: participantTable.filtered.length,
                  pageSize: participantTable.pageSize ?? participantTable.filtered.length,
                  previousLabel: t('box.organizer.pages.previous'),
                  nextLabel: t('box.organizer.pages.next'),
                  pageLabel: (value) => t('box.organizer.pages.page', { page: value }),
                }}
                testId="randomizer-participants"
              >
                {count > 0 ? (
                  <ParticipantTableRowList
                    placeholderCount={participantTable.placeholderCount}
                    testId="randomizer-participants-list"
                  >
                    {participantTable.visible.map((name) => (
                      <Row
                        key={name}
                        size="large"
                        align="center"
                        testId={`randomizer-exclusion-${name}`}
                      >
                        <Cluster
                          justify="between"
                          align="center"
                          gap={4}
                          wide
                          wrap={false}
                          className="participant-table__edit-row"
                        >
                          <Cluster
                            align="center"
                            gap={3}
                            wrap={false}
                            className="participant-table__edit-person"
                          >
                            <ParticipantFace index={draft.names.indexOf(name)} />
                            <Stack gap={1}>
                              <Typography as="p" role="title/sub">
                                {name}
                              </Typography>
                              <Typography as="p" role="meta/helper">
                                {exclusionSummary(name)}
                              </Typography>
                            </Stack>
                          </Cluster>
                          <Cluster
                            align="center"
                            gap={2}
                            wrap={false}
                            className="participant-table__edit-actions"
                          >
                            <Button
                              variant="soft"
                              size="small"
                              testId={`randomizer-configure-${name}`}
                              onClick={() => openEditor(name)}
                            >
                              {t('randomizer.exclusions.configure')}
                            </Button>
                            <Button
                              variant="ghost"
                              size="small"
                              icon={<CloseIcon />}
                              aria-label={t('randomizer.names.remove', { name })}
                              testId={`randomizer-remove-${name}`}
                              onClick={() => removeName(name)}
                            />
                          </Cluster>
                        </Cluster>
                      </Row>
                    ))}
                  </ParticipantTableRowList>
                ) : null}
              </ParticipantTable>
            </div>
          </Stack>
        </LandscapeIntro>

        <Stack gap={10} align="center" as="section" wide testId="randomizer-how">
          <Stack gap={3} align="center">
            <Typography as="h2" role="title/section">
              {t('randomizer.how.title')}
            </Typography>
            <Container width="measure">
              <Typography as="p" role="body/lead">
                {t('randomizer.how.lead')}
              </Typography>
            </Container>
          </Stack>
          <Columns columns={3} gap={5}>
            {HOW_STEPS.map((item) => (
              <PreviewCard
                key={item.id}
                tone={item.tone}
                step={item.step}
                title={t(`randomizer.how.${item.id}.title`)}
                testId={`randomizer-how-${item.step}`}
              >
                {t(`randomizer.how.${item.id}.body`)}
              </PreviewCard>
            ))}
          </Columns>
        </Stack>

        <Stack gap={6} align="center" as="section" wide testId="randomizer-benefits">
          <Typography as="h2" role="title/section">
            {t('randomizer.benefits.title')}
          </Typography>
          <Columns columns="fill" gap={5}>
            {BENEFIT_CARDS.map(({ id, tone, icon }) => (
              <Card
                key={id}
                tone={tone}
                icon={icon}
                surface="plain"
                title={t(`randomizer.benefits.${id}.title`)}
                testId={`randomizer-benefit-${id}`}
              >
                {t(`randomizer.benefits.${id}.body`)}
              </Card>
            ))}
          </Columns>
        </Stack>

        <Stack gap={5} as="section" wide testId="randomizer-faq">
          <Typography as="h2" role="title/section">
            {t('randomizer.faq.title')}
          </Typography>
          <ExpandableGroup defaultOpenIndex={0} testId="randomizer-faq-list">
            {faq.map((item) => (
              <Expandable key={item.question} title={item.question}>
                <Prose>
                  {paragraphs(item.answer).map((paragraph, index) => (
                    <p key={index}>{paragraph}</p>
                  ))}
                </Prose>
              </Expandable>
            ))}
          </ExpandableGroup>
        </Stack>

        <Card
          title={t('randomizer.full.title')}
          action={
            <Button
              variant="secondary"
              size="large"
              testId="randomizer-full-game"
              onClick={() => navigate('/box/new')}
            >
              {t('randomizer.full.button')}
            </Button>
          }
          testId="randomizer-full"
        >
          {t('randomizer.full.body')}
        </Card>
      </Stack>
      <FaqJsonLd items={faq.map((item) => ({ question: item.question, answer: item.answer }))} />
    </BoxShell>
  );
}
